import React, { ForwardedRef, forwardRef } from "react"
import { RadioButton } from "../../atoms/controls"
import styled, { css } from "styled-components"
import { RadioButtonOption } from "./RadioButtonOption"
import { Heading4 } from "../../atoms/text"
import { theme } from "../../../theme"
import { FocusableDiv } from "../../common/FocusableDiv"

export type RadioButtonGroupProps<TOptions extends RadioButtonOption[]> = {
  value: TOptions[number]["value"] | undefined
  onChange: (value: TOptions[number]["value"]) => void
  options: TOptions
  vertical?: boolean
  label?: string
  required?: boolean
  className?: string
}

const RadioButtonGroupInner = <TOptions extends RadioButtonOption[]>(
  props: RadioButtonGroupProps<TOptions>,
  ref: ForwardedRef<HTMLDivElement>
) => {
  const { value, options, vertical = false, label, required, className } = props

  const onChange = (newValue: string) => {
    props.onChange(newValue)
  }

  return (
    <FocusableDiv ref={ref}>
      {!!label && (
        <Heading4>
          {label}
          {required ? "*" : ""}
        </Heading4>
      )}
      <Root $vertical={vertical} className={className}>
        {options.map((option) => (
          <RadioButton
            key={option.value}
            name={option.value}
            label={option.label}
            checked={option.value === value}
            disabled={option.disabled}
            onChange={() => onChange(option.value)}
          />
        ))}
      </Root>
    </FocusableDiv>
  )
}

const Root = styled.div<{ $vertical: boolean }>`
  ${(p) =>
    p.$vertical &&
    css`
      display: flex;
      flex-direction: column;
    `}
  ${(p) =>
    !p.$vertical &&
    css`
      display: flex;
      gap: ${theme.spacing.l};
    `}
`

export const RadioButtonGroup = forwardRef(RadioButtonGroupInner)
